import CheckIcon from "@mui/icons-material/Check";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  alpha,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";

import type { AccountPersonField } from "../../event/server/supabase-account-person-field-service";
import type { InsightKeyFieldWithData } from "../server/supabase-insight-key-field-service";
import type { FC, ReactElement } from "react";

interface Props {
  open: boolean;
  mode: "add" | "edit";
  field: InsightKeyFieldWithData | null;
  personFields: AccountPersonField[];
  configuredKeys: string[];
  onClose: () => void;
  onSave: (fieldKey: string, displayName: string) => Promise<void>;
  onLoadPreview: (fieldKey: string) => Promise<string[] | undefined>;
  onLoadMetrics: (
    fieldKey: string,
  ) => Promise<{ coverage: number; cardinality: number } | undefined>;
}

type MetricRating = "good" | "moderate" | "bad";

const coverage = (value: number): MetricRating => {
  if (value >= 90) return "good";
  if (value >= 60) return "moderate";
  return "bad";
};

const cardinality = (value: number): MetricRating => {
  if (value < 5) return "good";
  if (value <= 20) return "moderate";
  return "bad";
};

const ratingIcon = (rating: MetricRating): ReactElement => {
  if (rating === "good") return <CheckIcon />;
  if (rating === "moderate") return <HelpOutlineIcon />;
  return <WarningAmberOutlinedIcon />;
};

const MetricChip: FC<{
  metric: "coverage" | "cardinality";
  value: number;
}> = ({ metric, value }) => {
  const rating = metric === "coverage" ? coverage(value) : cardinality(value);
  const color = rating === "good" ? "success" : rating === "moderate" ? "warning" : "error";

  return (
    <Chip
      icon={ratingIcon(rating)}
      label={`${metric === "coverage" ? "Coverage" : "Cardinality"}: ${rating}`}
      size="small"
      color={color}
      sx={(theme) => ({
        bgcolor: alpha(theme.palette[color].main, 0.12),
        color: theme.palette[color].dark,
      })}
    />
  );
};

export const InsightKeyFieldPanel: FC<Props> = ({
  open,
  mode,
  field,
  personFields,
  configuredKeys,
  onClose,
  onSave,
  onLoadPreview,
  onLoadMetrics,
}) => {
  // State initialised from props — parent remounts via `key` when panel opens/changes
  const [selectedKey, setSelectedKey] = useState(field?.field_key ?? "");
  const [displayName, setDisplayName] = useState(field?.display_name ?? "");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [previewExpanded, setPreviewExpanded] = useState(false);
  const [previewValues, setPreviewValues] = useState<string[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(false);
  const [metrics, setMetrics] = useState<{ coverage: number; cardinality: number }>();
  const [metricsLoading, setMetricsLoading] = useState(Boolean(selectedKey));
  const metricsRequest = useRef(0);

  useEffect(() => {
    if (!selectedKey) return;

    const request = ++metricsRequest.current;
    void onLoadMetrics(selectedKey)
      // oxlint-disable-next-line promise/always-return -- side-effect-only then() callback
      .then((result) => {
        if (request === metricsRequest.current) setMetrics(result);
      })
      .catch(() => {
        if (request === metricsRequest.current) setMetrics(undefined);
      })
      .finally(() => {
        if (request === metricsRequest.current) setMetricsLoading(false);
      });
  }, [onLoadMetrics, selectedKey]);

  const fetchPreview = async (key: string): Promise<void> => {
    setPreviewLoading(true);
    setPreviewError(false);
    try {
      const values = await onLoadPreview(key);
      setPreviewValues(values ?? []);
    } catch {
      setPreviewError(true);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleFieldChange = async (key: string): Promise<void> => {
    metricsRequest.current += 1;
    setMetrics(undefined);
    setMetricsLoading(true);
    setSelectedKey(key);
    const personField = personFields.find((f) => f.key === key);
    if (personField) setDisplayName(personField.display_name);
    setPreviewValues([]);
    setPreviewError(false);
    if (previewExpanded) await fetchPreview(key);
  };

  const handlePreviewToggle = async (): Promise<void> => {
    const next = !previewExpanded;
    setPreviewExpanded(next);
    if (next && selectedKey && previewValues.length === 0 && !previewError) {
      await fetchPreview(selectedKey);
    }
  };

  const handleSave = async (): Promise<void> => {
    if (!selectedKey || !displayName) return;
    setSaving(true);
    setSaveError(false);
    try {
      await onSave(selectedKey, displayName);
    } catch {
      setSaveError(true);
    } finally {
      setSaving(false);
    }
  };

  const isSelectable = (pf: AccountPersonField): boolean =>
    pf.segmentation_field && (pf.key === selectedKey || !configuredKeys.includes(pf.key));

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {mode === "add" ? "Add a field for our AI to analyze for insights" : "Edit field"}
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {saveError && <Alert severity="error">Something went wrong. Please try again.</Alert>}
          <Typography variant="body2">
            Select a field from your data, and our Insights AI will analyze survey responses and
            Communications engagement for patterns and trends emerging from different groups.
          </Typography>

          <FormControl fullWidth>
            <InputLabel>Field</InputLabel>
            <Select
              value={selectedKey}
              label="Field"
              onChange={(e) => void handleFieldChange(e.target.value)}
            >
              {personFields
                .filter(
                  (pf) =>
                    !pf.key.startsWith("event_") &&
                    !pf.key.startsWith("recipient_type_") &&
                    isSelectable(pf),
                )
                .map((pf) => (
                  <MenuItem key={pf.key} value={pf.key}>
                    {pf.display_name} ({pf.key})
                  </MenuItem>
                ))}
            </Select>
            {!selectedKey && (
              <FormHelperText>
                Select a field to see if it’s suitable for insight generation
              </FormHelperText>
            )}
          </FormControl>

          {selectedKey && (
            <Box sx={{ minHeight: 24 }}>
              {metricsLoading ? (
                <CircularProgress size={20} aria-label="Loading field metrics" />
              ) : metrics ? (
                <Stack direction="row" flexWrap="wrap" gap={1}>
                  <MetricChip metric="coverage" value={metrics.coverage} />
                  <MetricChip metric="cardinality" value={metrics.cardinality} />
                </Stack>
              ) : null}
            </Box>
          )}

          <Box>
            <Button
              variant="text"
              size="small"
              disabled={!selectedKey}
              data-analytics-id="insight-key-field-preview-data"
              endIcon={
                <ExpandMoreIcon
                  sx={{
                    transform: previewExpanded ? "rotate(180deg)" : "none",
                    transition: "transform 0.2s",
                  }}
                />
              }
              onClick={() => void handlePreviewToggle()}
            >
              Preview data
            </Button>
            <Collapse in={previewExpanded}>
              <Box sx={{ mt: 1 }}>
                {previewLoading ? (
                  <CircularProgress size={20} />
                ) : previewError || previewValues.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No preview available
                  </Typography>
                ) : (
                  <Stack direction="row" flexWrap="wrap" gap={1}>
                    {previewValues.map((v) => (
                      <Chip key={v} label={v} size="small" />
                    ))}
                  </Stack>
                )}
              </Box>
            </Collapse>
          </Box>

          <TextField
            fullWidth
            label="Display name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            helperText="This is the word the AI will use when showing insights involving this field"
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button data-analytics-id="insight-key-field-panel-cancel" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          data-analytics-id="insight-key-field-panel-save"
          onClick={() => void handleSave()}
          disabled={!selectedKey || !displayName || saving}
        >
          {saving ? <CircularProgress size={20} /> : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
